import type { InputHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';

/**
 * SearchField.
 *
 * §10 Phase 2 and the mockups both pin search to the top of every buyer screen,
 * because most sessions begin with a drug name copied off a doctor's note or
 * read off an empty pack. It is a 44px target and it is never behind an icon.
 */

export interface SearchFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  onClear?: () => void;
  containerClassName?: string;
}

export function SearchField({
  onClear,
  className,
  containerClassName,
  value,
  placeholder = 'Search medicines, conditions, brands',
  ...props
}: SearchFieldProps) {
  return (
    <div
      className={cn(
        'flex min-h-11 items-center gap-[9px] rounded-[10px] border border-line bg-white px-[13px] py-[11px]',
        'focus-within:border-blue focus-within:ring-2 focus-within:ring-blue/15',
        containerClassName,
      )}
    >
      <Icon name="search" size={17} strokeWidth={1.9} className="text-muted" />
      <input
        type="search"
        role="searchbox"
        value={value}
        placeholder={placeholder}
        className={cn(
          'w-full border-0 bg-transparent p-0 text-[13px] text-ink outline-none',
          'placeholder:text-faint [&::-webkit-search-cancel-button]:hidden',
          className,
        )}
        {...props}
      />
      {onClear && value ? (
        <button
          type="button"
          onClick={onClear}
          aria-label="Clear search"
          className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-faint hover:bg-paper hover:text-muted"
        >
          <Icon name="x" size={13} strokeWidth={2.4} />
        </button>
      ) : null}
    </div>
  );
}
